import { bisector, extent } from 'd3-array';
import { Axis, axisBottom, axisRight } from 'd3-axis';
import { format } from 'd3-format';
import { ScaleLinear, ScaleTime, scaleLinear, scaleTime } from 'd3-scale';
import { Selection, mouse, select, selectAll } from 'd3-selection';
import { Area, CurveFactory, Line, area, curveBasis, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';
import { forEach as _forEach } from 'es-toolkit/compat';

import { parseInteger } from '../models/helpers';

interface DataPoint {
  [key: string]: number | Date;
  expected: number;
  date: Date;
}

export type ProjectionStatus = 'onTrack' | 'offTrack';

interface ProjectionGraphSettings {
  margin: { left: number; top: number; right: number; bottom: number };
  graphHeight: number;
  tooltipClass: string;
  emptyMaxValue: number;
  interpolation: CurveFactory;
  width: number;
  height: number;
  goalDateExpectedProjectedBalance: number;
  valuesByDate: Array<DataPoint>;
  currentBalance: number;
  maxValue: number;
  minValue: number;
  target: number;
  containerElement: HTMLElement;
  svgElement: SVGElement;
  onTrack: boolean;
  termYears: number;
  isIncome: boolean;
  isMiniGraph: boolean;
  status: ProjectionStatus;
  xAxisBufferSpace: number;
}

type ProjectionGraphDefaults = Pick<
  ProjectionGraphSettings,
  'margin' | 'graphHeight' | 'tooltipClass' | 'interpolation' | 'emptyMaxValue'
>;

export type ProjectionGraphOptions = Partial<ProjectionGraphDefaults> &
  Omit<
    ProjectionGraphSettings,
    | 'containerElement'
    | 'svgElement'
    | 'width'
    | 'height'
    | 'goalDateExpectedProjectedBalance'
    | keyof ProjectionGraphDefaults
  >;

interface ProjectionGraphGenerators {
  xAxis: Axis<Date>;
  yAxis: Axis<number>;
  line: {
    [key: string]: Line<DataPoint>;
    target: Line<DataPoint>;
    projection: Line<DataPoint>;
    expected: Line<DataPoint>;
    poor: Line<DataPoint>;
    deposit: Line<DataPoint>;
  };
  area: {
    [key: string]: Area<DataPoint>;
    broadRange: Area<DataPoint>;
    midRange: Area<DataPoint>;
  };
}

interface ProjectionGraphScales {
  x: ScaleTime<number, number>;
  y: ScaleLinear<number, number>;
}

export default class ProjectionGraph {
  settings: ProjectionGraphSettings;

  generators: ProjectionGraphGenerators;

  scales: ProjectionGraphScales;

  container: Selection<HTMLElement, DataPoint, HTMLElement | null, undefined>;

  svg: Selection<SVGGElement, DataPoint, HTMLElement | null, undefined>;

  constructor(containerElement: HTMLElement, svgElement: SVGElement, options: ProjectionGraphOptions) {
    const elementWidth = svgElement.getBoundingClientRect().width;
    const DEFAULTS: ProjectionGraphDefaults = {
      margin: {
        top: 20,
        right: options.isMiniGraph ? 0 : 70,
        left: 0,
        bottom: options.isMiniGraph ? 30 : 50,
      },
      graphHeight: Math.min(0.75 * elementWidth, 375),
      tooltipClass: '.ProjectionGraph-tooltip',
      interpolation: curveBasis,
      emptyMaxValue: 1000000,
    };

    this.settings = {
      ...DEFAULTS,
      ...options,
      containerElement,
      svgElement,
      width: 0,
      height: 0,
      goalDateExpectedProjectedBalance: 0,
      xAxisBufferSpace: options.isMiniGraph ? 40 : 0,
    };
    this.settings.width = elementWidth - this.settings.margin.left - this.settings.margin.right;
    this.settings.height = this.settings.graphHeight - this.settings.margin.top - this.settings.margin.bottom;
    this.settings.goalDateExpectedProjectedBalance = this.settings.valuesByDate.slice(-1)[0].expected;
  }

  draw() {
    this.emptyLayout();
    this.setUpScales();
    this.setUpGenerators();
    this.createLayout();
    this.drawData();
    this.drawHoverBar();
    this.initHover();
  }

  emptyLayout() {
    const svg = this.settings.svgElement;
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }
  }

  setUpScales() {
    const yMin = Math.min(this.settings.currentBalance, Math.max(this.settings.minValue, 0), this.settings.target || 0);
    const yMax = Math.max(this.settings.maxValue, this.settings.target || 0) || this.settings.emptyMaxValue;

    this.scales = {
      x: scaleTime()
        .domain(
          extent<DataPoint, Date>(this.settings.valuesByDate, d => {
            return d.date;
          }) as [Date, Date]
        )
        .range([0, this.settings.width]),
      y: scaleLinear().domain([yMin, yMax]).range([this.settings.height, 0]).nice(),
    };
  }

  setUpGenerators() {
    const { scales } = this;
    const { target } = this.settings;
    const { interpolation } = this.settings;
    const { goalDateExpectedProjectedBalance } = this.settings;
    const axisDateFormat = timeFormat(this.settings.termYears > 10 ? '%Y' : '%-m/%Y');
    const xTicks = this.settings.width < 500 ? 3 : 6;
    const yTicks = this.settings.width < 500 ? 4 : 8;
    const tickSize = 10;

    const xAxisTickValues = () => {
      if (this.settings.isMiniGraph) {
        const formatDate = timeFormat('%Y');
        return axisBottom<Date>(scales.x)
          .tickValues(scales.x.domain())
          .tickSize(0)
          .tickPadding(8)
          .tickFormat((d: Date) => (d.getTime() === scales.x.domain()[0].getTime() ? 'Today' : formatDate(d)));
      }

      return axisBottom<Date>(scales.x).tickSize(tickSize).ticks(xTicks).tickPadding(8).tickFormat(axisDateFormat);
    };

    this.generators = {
      xAxis: xAxisTickValues(),
      yAxis: axisRight<number>(scales.y).tickSize(tickSize).ticks(yTicks).tickPadding(10).tickFormat(format('($.3s')),
      line: {
        target: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(scales.y(target) as number),
        projection: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(scales.y(goalDateExpectedProjectedBalance) as number),
        expected: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(d => scales.y(d.expected) as number)
          .curve(interpolation),
        poor: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(d => scales.y(d.lowestPercentile) as number)
          .curve(interpolation),
        deposit: line<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y(d => scales.y(d.deposit) as number),
      },
      area: {
        broadRange: area<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y0(d => scales.y(d.lowestPercentile) as number)
          .y1(d => scales.y(d.highestPercentile) as number)
          .curve(interpolation),
        midRange: area<DataPoint>()
          .x(d => scales.x(d.date) as number)
          .y0(d => scales.y(d.lowMidPercentile) as number)
          .y1(d => scales.y(d.highMidPercentile) as number)
          .curve(interpolation),
      },
    };
  }

  createLayout() {
    this.container = select(this.settings.containerElement);

    this.svg = this.container
      .select('svg')
      .attr('width', this.settings.width + this.settings.margin.left + this.settings.margin.right)
      .attr('height', this.settings.height + this.settings.margin.top + this.settings.margin.bottom)
      // @ts-ignore We need to confirm how this graph can be manually tested before changing this to this.svg.node()
      .on('mousemove', () => this.updateHover(this.settings.svgElement))
      .on('mouseout', () => this.hideHover())
      .append('g')
      .attr('transform', `translate(${this.settings.margin.left},${this.settings.margin.top})`);

    this.svg
      .append('g')
      .attr('class', 'ProjectionGraph-axis ProjectionGraph-axis--x')
      .attr('transform', `translate(0,${this.settings.height})`)
      .call(this.generators.xAxis);

    this.svg
      .append('g')
      .attr('class', 'ProjectionGraph-axis ProjectionGraph-axis--y')
      .attr('transform', `translate(${this.settings.width} , 0)`)
      .call(this.generators.yAxis);

    this.svg.selectAll('g.tick').attr('class', 'ProjectionGraph-axisTick ProjectionGraph-label');

    if (this.settings.isMiniGraph) {
      const graphWidth = this.settings.width;
      this.svg.selectAll('.ProjectionGraph-axis--x .ProjectionGraph-axisTick').each(function positionXAxisLabels(d, i) {
        if (i === 0) {
          select(this).attr('transform', 'translate(20,0)');
        }

        if (i === 1) {
          select(this).attr('transform', `translate(${graphWidth - 16},0)`);
        }
      });
    }
  }

  drawTargetLine() {
    const { height, onTrack, target, xAxisBufferSpace } = this.settings;
    const yVal = this.scales.y(target) || 0;
    const isOverlapping = height - xAxisBufferSpace < yVal;
    const yPos = isOverlapping || !onTrack ? yVal - 10 : yVal + 18;

    if (!target) {
      return;
    }

    this.drawLine('target');
    this.svg.append('text').attr('class', 'ProjectionGraph-label').attr('x', 0).attr('y', yPos).text('Target');
  }

  drawData() {
    this.drawArea('broadRange');
    this.drawArea('midRange');

    this.drawTargetLine();

    this.drawLine('expected');
    this.drawLine('deposit');

    this.drawCircle('expected');
    if (!this.settings.isIncome) {
      this.drawLine('deposit');
      this.drawCircle('deposit');
    }
  }

  drawArea(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.valuesByDate)
      .attr(
        'class',
        `ProjectionGraph-area ProjectionGraph-area--${type} ProjectionGraph-area--${this.settings.status}--${type}`
      )
      .attr('d', this.generators.area[type]);
  }

  drawLine(type: string) {
    this.svg
      .append('path')
      .datum(this.settings.valuesByDate)
      .attr(
        'class',
        `ProjectionGraph-line ProjectionGraph-line--${type} ProjectionGraph-line--${this.settings.status}--${type}`
      )
      .attr('d', this.generators.line[type]);
  }

  drawCircle(type: string) {
    this.svg
      .append('circle')
      .attr('r', 3)
      .attr(
        'class',
        `ProjectionGraph-hover ProjectionGraph-circle ProjectionGraph-circle--${type} ProjectionGraph-circle--${this.settings.status}--${type}`
      )
      .attr('data-type', type);
  }

  drawHoverBar() {
    this.svg.append('path').attr('class', 'ProjectionGraph-hover ProjectionGraph-bar');
  }

  initHover() {
    const lastPoint = this.settings.valuesByDate.slice(-1)[0];
    this.updateHoverBar(lastPoint);
    this.updateTooltip({ point: lastPoint, mouseX: 0 });
    this.updateCircles(lastPoint);
    this.hideHover();
  }

  updateHoverBar(point: DataPoint) {
    const x = this.scales.x(point.date);
    this.container
      .select('.ProjectionGraph-hover.ProjectionGraph-bar')
      .classed('ProjectionGraph-hover--show', true)
      .attr('d', `M${x || ''},${this.settings.height}L${x || ''},${0}`);
  }

  updateTooltip(options: { point: DataPoint; mouseX: number }) {
    const tooltip = this.settings.containerElement.querySelector(this.settings.tooltipClass) as HTMLElement;
    const { point } = options;
    const toolWidth = tooltip.getBoundingClientRect().width;
    const offset = options.mouseX - toolWidth;
    const leftMargin = this.settings.margin.left;

    tooltip.querySelector('.ProjectionGraph-tooltipDate')!.innerHTML = timeFormat('%B %Y')(
      // @ts-ignore We need a way to manually test this before getting rid of the parseInteger to match the DataPoint interface
      new Date(parseInteger(point.date))
    );

    _forEach(tooltip.querySelectorAll('.ProjectionGraph-tooltipValue'), item => {
      const type = item.getAttribute('data-value-type')!;
      item.innerHTML = format('($,.0f')(point[type]);
    });

    const leftValue = (this.scales.x(point.date) || 0) + leftMargin - (offset > 0 ? toolWidth - 1 : 1);
    tooltip.style.left = `${leftValue}px`;
    tooltip.classList.add('ProjectionGraph-hover--show');
  }

  updateCircles(point: DataPoint) {
    this.container
      .selectAll('.ProjectionGraph-circle')
      .classed('ProjectionGraph-hover--show', true)
      .attr('cx', this.scales.x(point.date) || 0);
    _forEach(document.querySelectorAll('.ProjectionGraph-circle'), circle => {
      const type = circle.getAttribute('data-type')!;
      circle.setAttribute('cy', String(this.scales.y(point[type])));
    });
  }

  updateHover(el: SVGGElement) {
    const container = el;
    const mouseX = mouse(container)[0] - this.settings.margin.left;
    const mouseY = mouse(container)[1];

    if (mouseX >= 0 && mouseX <= this.settings.width && mouseY >= 0 && mouseY <= this.settings.height) {
      const exactDate = this.scales.x.invert(mouseX);
      const point = this.getClosestDataPoint(exactDate);

      this.updateHoverBar(point);
      this.updateTooltip({
        point,
        mouseX,
      });
      this.updateCircles(point);
    } else {
      this.hideHover();
    }
  }

  getClosestDataPoint(date: Date) {
    const bisect = bisector<DataPoint, Date>(d => d.date).right; // eslint-disable-line @typescript-eslint/unbound-method
    const index = Math.min(bisect(this.settings.valuesByDate, date), this.settings.valuesByDate.length - 1);

    return this.settings.valuesByDate[index];
  }

  hideHover() {
    selectAll('.ProjectionGraph-hover').classed('ProjectionGraph-hover--show', false);
  }
}
